<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import { useCommercialDashboardStore } from '@/stores/useCommercialDashboardStore'
import { memberFullName } from './dashboardFormat'

const store = useCommercialDashboardStore()
const { viewMode, currentData, teamData, selectedMember, clientScope, dashboardTab, scopeCounts, isManager, syncing } =
  storeToRefs(store)

const pageTitle = computed(() => {
  if (viewMode.value === 'member' && selectedMember.value) {
    return `${selectedMember.value.firstName ?? ''} ${selectedMember.value.lastName ?? ''}`.trim()
  }
  if (viewMode.value === 'team') return 'Mon équipe'
  return 'Portefeuille Client'
})

const pageSubtitle = computed(() => {
  if (viewMode.value === 'team') return `${teamData.value?.members.length ?? 0} commerciaux`
  return `${currentData.value?.kpis.totalClients ?? 0} clients assignés`
})

const syncMe = () => {
  if (!window.confirm(
    "Cette action va aussi supprimer les sociétés de ton portefeuille dont tu " +
    "n'es plus le vendeur côté Odoo. Continuer ?",
  )) return
  store.sync('odoo-me-cleanup')
}
</script>

<template>
  <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
      <Bs2Title :title="pageTitle" :subtitle="pageSubtitle" />

      <!-- Onglets : Mes clients (propriétaire) / Tous -->
      <div v-if="currentData" class="join w-fit rounded-lg border border-base-300 bg-base-100 p-1">
        <button
          class="join-item btn btn-sm gap-2 border-0"
          :class="clientScope === 'me' && dashboardTab === 'portfolio' ? 'btn-primary' : 'btn-ghost'"
          @click="clientScope = 'me'; dashboardTab = 'portfolio'"
        >
          Mes clients
          <span
            class="badge badge-sm border-0"
            :class="clientScope === 'me' && dashboardTab === 'portfolio' ? 'bg-primary-content/25 text-primary-content' : 'bg-base-300'"
          >{{ scopeCounts.me }}</span>
          <span class="tooltip tooltip-bottom cursor-help text-xs font-normal opacity-80" data-tip="Sociétés dont je suis désigné propriétaire (responsable sur la fiche Odoo).">ⓘ</span>
        </button>
        <button
          class="join-item btn btn-sm gap-2 border-0"
          :class="clientScope === 'all' && dashboardTab === 'portfolio' ? 'btn-primary' : 'btn-ghost'"
          @click="clientScope = 'all'; dashboardTab = 'portfolio'"
        >
          Tous
          <span
            class="badge badge-sm border-0"
            :class="clientScope === 'all' && dashboardTab === 'portfolio' ? 'bg-primary-content/25 text-primary-content' : 'bg-base-300'"
          >{{ scopeCounts.all }}</span>
          <span class="tooltip tooltip-bottom cursor-help text-xs font-normal opacity-80" data-tip="Mes clients affectés + les clients à qui j'ai déjà vendu (via une commande).">ⓘ</span>
        </button>
        <button
          class="join-item btn btn-sm gap-2 border-0"
          :class="clientScope === 'orders' && dashboardTab === 'portfolio' ? 'btn-primary' : 'btn-ghost'"
          @click="clientScope = 'orders'; dashboardTab = 'portfolio'"
        >
          Commandes
          <span
            class="badge badge-sm border-0"
            :class="clientScope === 'orders' && dashboardTab === 'portfolio' ? 'bg-primary-content/25 text-primary-content' : 'bg-base-300'"
          >{{ scopeCounts.orders }}</span>
          <span class="tooltip tooltip-bottom cursor-help text-xs font-normal opacity-80" data-tip="Sociétés où j'ai déjà vendu (commande) mais où je ne suis pas désigné sur la fiche Odoo.">ⓘ</span>
        </button>
        <button
          class="join-item btn btn-sm gap-2 border-0"
          :class="dashboardTab === 'quote-billing' ? 'btn-primary' : 'btn-ghost'"
          @click="dashboardTab = 'quote-billing'"
        >
          Mes devis & factures
          <span class="tooltip tooltip-bottom cursor-help text-xs font-normal opacity-80" data-tip="Devis dont je suis l'auteur, et leurs factures rattachées — filtrable par statut de paiement.">ⓘ</span>
        </button>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <!-- Sélecteur de vue (manager uniquement) -->
      <template v-if="isManager">
        <button
          class="btn btn-sm"
          :class="viewMode === 'me' ? 'btn-primary' : 'btn-ghost'"
          @click="store.fetchMyDashboard()"
        >
          Moi
        </button>
        <button
          class="btn btn-sm"
          :class="viewMode === 'team' ? 'btn-primary' : 'btn-ghost'"
          @click="store.fetchTeamDashboard()"
        >
          Mon équipe
        </button>
        <template v-if="viewMode === 'member' && selectedMember">
          <span class="flex items-center px-2 text-sm text-base-content/50">›</span>
          <button class="btn btn-sm btn-primary">
            {{ memberFullName(selectedMember) }}
          </button>
        </template>
        <div class="divider divider-horizontal mx-0 hidden sm:flex"></div>
      </template>

      <!-- Boutons synchronisation -->
      <div class="flex items-center gap-2">
        <button
          class="btn btn-sm btn-ghost gap-1.5"
          :disabled="syncing"
          title="Resynchronise mes sociétés et supprime celles dont je ne suis plus le vendeur Odoo"
          @click="syncMe"
        >
          <span v-if="syncing" class="loading loading-spinner loading-xs"></span>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Synchroniser mes sociétés
        </button>

        <button
          v-if="isManager"
          class="btn btn-sm btn-ghost gap-1.5"
          :disabled="syncing"
          @click="store.sync('odoo-team')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Synchroniser les sociétés de mon équipe
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Tooltips daisyUI : fond plein (le thème laissait un fond transparent). */
.tooltip {
  --tooltip-color: #1f2937; /* slate-800 */
  --tooltip-text-color: #ffffff;
}
.tooltip::before {
  background-color: #1f2937;
  color: #ffffff;
}
</style>
